import { useEffect, useRef } from "react";

export function useWakeLock() {
  const wakeLockRef = useRef(null);

  useEffect(() => {
    async function requestWakeLock() {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
          console.log("✅ [WakeLock] Screen wake lock activated");

          // Listen for release events (when system suspends, tab hidden, etc.)
          wakeLockRef.current.addEventListener("release", () => {
            console.log("⚠️ [WakeLock] Screen wake lock was released (screen might go dark)");
          });

          // Reacquire if user returns to the page
          document.addEventListener("visibilitychange", async () => {
            if (document.visibilityState === "visible") {
              console.log("🔄 [WakeLock] Tab visible again — reactivating wake lock...");
              try {
                wakeLockRef.current = await navigator.wakeLock.request("screen");
                console.log("✅ [WakeLock] Reacquired after visibility change");
              } catch (err) {
                console.error("❌ [WakeLock] Failed to reacquire:", err);
              }
            }
          });
        } else {
          console.warn("❌ [WakeLock] API not supported — using fallback");
          fallbackKeepAwake();
        }
      } catch (err) {
        console.error("❌ [WakeLock] Error:", err);
      }
    }

    requestWakeLock();

    // Cleanup
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log("🧹 [WakeLock] Released on unmount");
      }
    };
  }, []);
}

// Fallback for older iOS (before 16.4)
function fallbackKeepAwake() {
  const video = document.createElement("video");
  video.src = "/blank.mp4"; // short muted looping video
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.style.display = "none";
  document.body.appendChild(video);
  video.play()
    .then(() => console.log("🎬 [WakeLock] Fallback video playing to keep screen awake"))
    .catch((err) => console.warn("⚠️ [WakeLock] Fallback video play failed:", err));
}
